import posthog, { type BeforeSendFn } from 'posthog-js';

// PostHog "group" types — names are part of the analytics contract.
// Dashboards, insights, and funnels in PostHog reference them by exact string.
export const POSTHOG_GROUP_TYPES = {
  PRODUCT: 'product',
  SITE: 'site',
  PRACTICE: 'practice',
  PARTNER: 'partner',
  ORDER: 'order',
} as const;

type PosthogGroupType =
  (typeof POSTHOG_GROUP_TYPES)[keyof typeof POSTHOG_GROUP_TYPES];

// Patch semantics: only keys present in the argument are modified.
//   - { order: 'abc' }    → add/replace order
//   - { order: undefined} → clear order
//   - { product, site }   → update those two, leave the rest alone
// We register groups manually rather than calling posthog.group(),
// which would also fire group.identify events from the client. Group
// identification is owned by the backend.
export function setPosthogGroups(
  groups: Partial<Record<PosthogGroupType, string | undefined>>,
) {
  const next: Record<string, string> = { ...posthog.getGroups() };
  for (const groupType of Object.values(POSTHOG_GROUP_TYPES)) {
    if (!(groupType in groups)) continue;
    const value = groups[groupType];
    if (value) {
      next[groupType] = value;
    } else {
      delete next[groupType];
    }
  }
  posthog.register({ $groups: next });
}

// Unmasked if the element opts in with [data-observe] attribute.
// Used for both text and input masking in session replay.
export const unmaskIfObserved = (text: string, element?: HTMLElement) =>
  element?.closest('[data-observe]') ? text : '***';

// Copy the clicked element's data-track value to a top-level `tracked_element`
// property so $autocapture clicks can be filtered/broken-down by it in PostHog.
// We parse $elements_chain (the serialized element string posthog-js sends with
// every $autocapture) — the $elements array PostHog's UI shows is reconstructed
// server-side and isn't available on the event in before_send. The chain
// includes attributes from target → root, so a click on a child icon still
// resolves the parent button's data-track. Does NOT rename the event.
export const addTrackedElementProperty: BeforeSendFn = (event) => {
  if (event?.event !== '$autocapture') return event;
  const chain = event.properties?.$elements_chain as string | undefined;
  const dataTrack = chain?.match(/attr__data-track="([^"]+)"/)?.[1];
  if (dataTrack) {
    event.properties.tracked_element = dataTrack;
  }
  return event;
};

export function getSoftwareVersion(): string | undefined {
  const version = import.meta.env.VITE_SOFTWARE_VERSION;
  if (!version) return undefined;
  return version;
}

export function registerSoftwareVersion() {
  const version = getSoftwareVersion();
  if (version) {
    posthog.register({ $app_version: version });
  }
}

export function initPosthog() {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST;
  if (!apiKey || !apiHost) {
    console.warn(
      'VITE_POSTHOG_KEY and VITE_POSTHOG_HOST must both be set; PostHog will not be initialized.',
    );
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,

    before_send: addTrackedElementProperty,

    loaded: registerSoftwareVersion,

    // Every event creates/updates a Person record (anonymous or identified).
    // On identify(), posthog-js sends $anon_distinct_id so PostHog merges the
    // pre-login anonymous Person into the identified one — necessary because
    // the boot-time $pageview fires before App.tsx's identify effect runs.
    person_profiles: 'always',

    // Autocapture is restricted to elements explicitly opted in with [data-track].
    autocapture: { css_selector_allowlist: ['[data-track]'] },

    capture_exceptions: true,

    capture_pageview: true,
    capture_pageleave: true,

    // Full session replay, mask-by-default.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      // maskTextSelector forces every text node to go through maskTextFn,
      // which then unmasks only the [data-observe] opt-in subtree.
      maskTextSelector: '*',
      maskTextFn: unmaskIfObserved,
      maskInputFn: unmaskIfObserved,
    },
  });
}

export default posthog;
